"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { normalizeSubCategory } from "@/lib/normalizeSubCategory";
import { getProductPath } from "@/lib/productPaths";
import { formatProductCardTag } from "@/lib/formatProductCardTag";
import { formatProductCardCategoryTag } from "@/lib/formatProductCardCategoryTag";

const PAGE_SIZE = 12;

function getLowestPrice(product) {
  const prices = [];
  product.options.forms.forEach((form) => {
    form.strengths?.forEach((strength) => {
      strength.counts?.forEach((c) => prices.push(c.price));
    });
  });
  return prices.length ? Math.min(...prices).toFixed(2) : null;
}

export default function InfiniteProductGrid({
  products = [],
  category = "",
  subCategoryLabel = "",
  pageSize = PAGE_SIZE,
}) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [products, pageSize]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= products.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) =>
            Math.min(count + pageSize, products.length)
          );
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [products.length, visibleCount, pageSize]);

  const visibleProducts = products.slice(0, visibleCount);
  const hasMore = visibleCount < products.length;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {visibleProducts.map((product) => {
          const lowestPrice = getLowestPrice(product);
          const sub = normalizeSubCategory(product["sub-category"]);
          const subLabel = subCategoryLabel || (sub ? formatProductCardTag(sub) : "");

          return (
            <article
              key={product.slug}
              className="group relative flex flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-[#f5fbf6] via-white to-[#ecf7ff] ring-1 ring-green-100 shadow-[0_14px_40px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_60px_rgba(0,0,0,0.12)]"
            >
              <div className="absolute inset-x-6 top-4 h-10 rounded-full bg-gradient-to-r from-[#48A018]/15 via-[#01A6DE]/10 to-transparent blur-3xl opacity-80" aria-hidden="true" />

              <div className="flex items-center justify-between px-4 pt-4">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#2f4f2f] bg-white/80 border border-[#9fce8a]/60 rounded-full px-3 py-1">
                  {formatProductCardCategoryTag(category || product.category)}
                </span>
                {subLabel && (
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#0b6a73] bg-white/80 border border-[#01A6DE]/30 rounded-full px-3 py-1">
                    {subLabel}
                  </span>
                )}
              </div>

              <div className="relative mt-3 mx-4 rounded-xl bg-white/80 border border-[#e7f3e9] aspect-[4/3] flex items-center justify-center overflow-hidden">
                <img
                  src={`/images/products/${product.image}`}
                  alt={product.name}
                  className="max-w-[80%] max-h-[80%] object-contain transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-300" aria-hidden="true" />
              </div>

              <div className="flex flex-col flex-1 px-4 pb-4 mt-4 gap-3">
                <h3 className="text-lg font-bold text-[#1f3d1f] leading-tight">
                  <Link
                    href={getProductPath(product)}
                    className="hover:text-[#0369a1] transition-colors"
                  >
                    {product.name}
                  </Link>
                </h3>

                <p className="text-sm text-[#4a4a4a] leading-relaxed">
                  {product.description}
                </p>

                <div className="mt-auto flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-[0.08em] text-[#2f4f2f] font-semibold">
                      {lowestPrice ? "From" : "Call for pricing"}
                    </span>
                    {lowestPrice && (
                      <span className="text-2xl font-extrabold text-[#48A018]">
                        ${lowestPrice}
                      </span>
                    )}
                  </div>

                  <Link
                    href={getProductPath(product)}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0a5a3c] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#0a5a3c]/25 transition-all duration-200 hover:bg-[#094c33] hover:shadow-[#0a5a3c]/35 hover:-translate-y-0.5"
                  >
                    View Details
                    <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1">
                      →
                    </span>
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div ref={sentinelRef} className="h-10" aria-hidden="true" />

      {hasMore && (
        <div className="py-6 text-center text-sm text-gray-500">
          Loading more products...
        </div>
      )}
    </>
  );
}
